/**
 * Splitting a drop into requests the server will actually accept.
 *
 * Two ceilings, and the one people hit is not the one they expect: `max_batch_files`
 * caps the count, `max_upload_bytes` caps the **sum of one request body**. A thousand
 * 2 MB PDFs is a thousand files and two gigabytes, and it is the second number that
 * refuses it.
 *
 * Pure, so the packing is testable without a DOM or a server: it takes sizes and gives
 * back index groups.
 */

/** A file too large to ever fit in one request. Reported, never silently dropped. */
export interface Oversized {
  index: number;
  size: number;
}

export interface Packed<T> {
  batches: T[][];
  /** Files that exceed the whole-request ceiling on their own. */
  oversized: Oversized[];
}

/**
 * Greedy first-fit in the order given: files keep their drop order, so a progress count
 * matches what a person sees in the picker.
 *
 * `budget` is the byte ceiling less a margin for multipart framing — boundaries, headers
 * and filenames all ride in the same body, and a pack that exactly hits the limit is a
 * 413 for the sake of a few hundred bytes.
 */
export function packBatches<T>(
  items: readonly T[],
  sizeOf: (item: T) => number,
  maxFiles: number,
  budget: number,
): Packed<T> {
  const batches: T[][] = [];
  const oversized: Oversized[] = [];
  let current: T[] = [];
  let bytes = 0;

  items.forEach((item, index) => {
    const size = sizeOf(item);
    if (size > budget) {
      oversized.push({ index, size });
      return;
    }
    const wouldOverflow = bytes + size > budget || current.length >= Math.max(1, maxFiles);
    if (wouldOverflow && current.length > 0) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(item);
    bytes += size;
  });
  if (current.length > 0) batches.push(current);

  return { batches, oversized };
}

/** The share of the byte ceiling a batch may fill, leaving room for multipart framing. */
export const BODY_MARGIN = 0.9;
